export const supplierPageData = {
  stats: [
    { value: "60%", label: "Supply Chain Risk Reduction" },
    { value: "97%+", label: "On-Time Delivery Rate" },
    { value: "80%", label: "More Procurement Efficiency" },
    { value: "70%", label: "Faster Supplier Onboarding" },
  ],
  statsAccent: "from-sky-600 to-blue-700",

  challengeTitle: "Fragmented Supplier Management Is Holding Your Business Back",
  challengeSubtitle:
    "Spreadsheets, emails, and guesswork aren't supplier management — they're supply chain risks waiting to happen.",
  challenges: [
    {
      icon: "📧",
      title: "Supplier Communication Chaos",
      description:
        "Managing suppliers through email threads and phone calls creates miscommunications, missed follow-ups, and order errors.",
    },
    {
      icon: "❓",
      title: "No Objective Supplier Scoring",
      description:
        "Without performance data, you can't identify your best suppliers or make informed sourcing decisions.",
    },
    {
      icon: "⚠️",
      title: "Supply Chain Disruption Blindness",
      description:
        "You only learn about supplier problems after they've already disrupted your operations and cost you money.",
    },
    {
      icon: "📋",
      title: "Manual Compliance Tracking",
      description:
        "Chasing expired certifications and compliance documents manually is time-consuming and error-prone.",
    },
    {
      icon: "🔍",
      title: "Limited Supplier Discovery",
      description:
        "Finding and qualifying new suppliers for new categories or markets takes months of manual research.",
    },
    {
      icon: "💸",
      title: "No Spend Transparency",
      description:
        "Without consolidated spend data, you're overpaying for goods and missing negotiation leverage every day.",
    },
  ],
  theme: {
    challengeBg: "bg-slate-900",
    challengeTitle: "text-white",
    challengeSubtitle: "text-slate-400",
    challengeCard: "bg-slate-800/60",
    challengeBorder: "border-slate-700",
    badgeBg: "bg-sky-50 text-sky-700 border-sky-100",
    solutionIconBg: "bg-sky-50",
    solutionIconColor: "text-sky-600",
    solutionAccent: "from-sky-600 to-blue-600",
  },

  solutionTitle: "Build, Manage, and Optimise Your Supplier Network with AI",
  solutionSubtitle:
    "One intelligent platform for every supplier relationship — from discovery and onboarding to performance management and risk monitoring.",
  solutions: [
    {
      icon: "🔎",
      title: "AI Supplier Discovery",
      description:
        "Find verified suppliers by product category, geography, and certification — with AI matching you to the best-fit options.",
    },
    {
      icon: "⚡",
      title: "Digital Supplier Onboarding",
      description:
        "Automated document collection, compliance verification, and system provisioning reduces onboarding from months to days.",
    },
    {
      icon: "⭐",
      title: "Multi-Dimensional Performance Scoring",
      description:
        "Every supplier scored on delivery reliability, quality, pricing, and responsiveness — objectively, in real time.",
    },
    {
      icon: "🚨",
      title: "Proactive Risk Alerts",
      description:
        "AI monitors supplier health, market signals, and compliance status — alerting you to risks before they cause disruptions.",
    },
    {
      icon: "📋",
      title: "Automated Compliance Management",
      description:
        "Certifications, audits, and regulatory documents are tracked automatically with alerts when renewals are due.",
    },
    {
      icon: "💬",
      title: "Centralised Supplier Communication",
      description:
        "All supplier interactions, RFQs, and order communications in one place — with full audit trail and search.",
    },
    {
      icon: "📊",
      title: "Spend Analytics & Benchmarking",
      description:
        "See exactly how much you spend with every supplier and compare against market benchmarks to find savings.",
    },
    {
      icon: "🔄",
      title: "Preferred Supplier Programmes",
      description:
        "Build and manage preferred supplier lists that automatically route orders to your best-performing vendors.",
    },
    {
      icon: "🌐",
      title: "Global Supplier Network Access",
      description:
        "Tap into EkkBaz AI's network of verified global suppliers to diversify and strengthen your supply base.",
    },
  ],

  howItWorksTitle: "From Fragmented to Fully Managed in 4 Steps",
  howItWorksSteps: [
    {
      icon: "📥",
      title: "Import Your Suppliers",
      description: "Upload existing supplier data or connect your ERP — all supplier records migrated in minutes.",
    },
    {
      icon: "⭐",
      title: "AI Scores Performance",
      description: "The AI engine immediately begins scoring every supplier on delivery, quality, and pricing data.",
    },
    {
      icon: "🚨",
      title: "Risks Are Flagged",
      description: "AI surfaces underperforming suppliers and proactively alerts you to compliance or risk issues.",
    },
    {
      icon: "📈",
      title: "Supply Chain Improves",
      description: "Route more orders to high scorers, address low performers, and continuously strengthen your supply base.",
    },
  ],

  outcomeTitle: "A Stronger, More Resilient Supply Chain",
  outcomeSubtitle: "The measurable supply chain improvements businesses achieve with EkkBaz AI.",
  outcomes: [
    {
      value: "60%",
      metric: "Supply Chain Risk Reduction",
      description:
        "AI-powered risk monitoring and proactive disruption management reduces supply chain incidents by 60%.",
    },
    {
      value: "97%+",
      metric: "On-Time Delivery",
      description:
        "Routing orders to top-performing suppliers — identified by AI scoring — improves on-time delivery to 97%+.",
    },
    {
      value: "80%",
      metric: "More Efficient Procurement",
      description:
        "Automation of supplier discovery, onboarding, and compliance management drives 80% greater procurement efficiency.",
    },
  ],
  outcomeImage: "/assets/images/products/supplier2.png",

  ctaTitle: "Build the Supply Chain Your Business Deserves",
  ctaSubtitle:
    "Book a free consultation to see how EkkBaz AI can reduce your supply chain risk by 60% and drive 97%+ on-time delivery.",
  ctaLabel: "Book a Free Consultation",
  ctaLink: "https://outlook.office.com/book/EkkBazInvestment@EkkBaz.com/?ismsaljsauthenabled",
};

export const supplierData = [
  {
    id: 1,
    extraLabel: "The Challenge",
    userType: "Fragmented Supplier Relationships Are Hurting Your Supply Chain",
    description:
      "Managing suppliers across spreadsheets, emails, and phone calls makes it impossible to maintain consistent quality, negotiate effectively, or respond quickly to supply disruptions. You lack the data to know which suppliers are performing, and you're missing the visibility to build truly resilient procurement operations.",
    banner: "/assets/images/products/supplier1.png",
  },
  {
    id: 2,
    extraLabel: "Our Solution",
    userType: "Build, Manage, and Optimize Your Supplier Network with AI",
    description:
      "EkkBaz AI centralizes every supplier relationship in one intelligent platform. Discover and onboard new suppliers digitally, manage product-level performance scoring, track every order from placement to delivery, and communicate with suppliers in real time. AI automatically scores each supplier on delivery reliability, quality, pricing competitiveness, and responsiveness — giving you the data to build a best-in-class supplier base.",
    banner: "/assets/images/products/supplier2.png",
  },
  {
    id: 3,
    extraLabel: "The Outcome",
    userType: "Reduce Supplier Risks 60% and Increase Procurement Efficiency 80%",
    description:
      "EkkBaz AI-powered supplier management reduces supply chain disruptions by 60%, improves on-time delivery rates to 97%+, and increases procurement efficiency by 80% through intelligent automation. AI-driven supplier scoring and market benchmarking ensure you always source from the best suppliers at the best prices.",
    banner: "/assets/images/products/supplier3.png",
  },
];

export const supplierFAQs = [
  {
    question: "How does EkkBaz AI score and evaluate suppliers?",
    answer:
      "EkkBaz AI continuously scores every supplier across four dimensions: delivery reliability, quality, pricing competitiveness, and responsiveness — giving you an objective, data-driven view of every supplier.",
  },
  {
    question: "How does EkkBaz AI help find and qualify new suppliers?",
    answer:
      "EkkBaz AI's supplier discovery engine helps you find verified suppliers by product category, geography, and certification level. Digital qualification workflows automate document collection and compliance verification, reducing new supplier onboarding time by 70%.",
  },
  {
    question: "Can EkkBaz AI manage supplier contracts and compliance documentation?",
    answer:
      "Yes. EkkBaz AI provides a centralized document management system for all supplier contracts, certifications, and compliance documents, with automated alerts when documents are approaching expiration.",
  },
];
